
import React, { useState, useEffect } from 'react';
import { Lead, JobStatus, User } from '../types';
import { Icons } from '../constants';

interface Props {
  onAddLead: (lead: Lead) => Promise<boolean | void> | boolean | void;
  onClose: () => void;
  onReturn?: () => void;
  user?: User;
}

const CustomerIntakeForm: React.FC<Props> = ({ onAddLead, onClose, onReturn, user }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    propertyType: 'Residential',
    serviceType: 'Pruning',
    urgency: 'Medium',
    source: 'Search Engine',
    notes: '',
    attachments: [] as string[]
  });

  useEffect(() => {
    if (user) {
      const [firstName, ...lastNames] = user.name.split(' ');
      setFormData(prev => ({
        ...prev,
        firstName: firstName || '',
        lastName: lastNames.join(' ') || '',
        email: user.email || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const files = Array.from(e.target.files);
    
    const newAttachments = await Promise.all(files.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    }));

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newLead: Lead = {
      id: `CST-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
      clientName: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      serviceType: formData.serviceType as Lead['serviceType'],
      status: JobStatus.LEAD,
      createdAt: new Date().toISOString(),
      attachments: formData.attachments
    };
    const success = onAddLead(newLead);
    if (success !== false) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white p-12 rounded-3xl shadow-2xl text-center max-w-lg mx-auto border border-stone-100">
        <div className="w-20 h-20 bg-leaf/20 text-palm rounded-full flex items-center justify-center mx-auto mb-6">
          <Icons.Safety />
        </div>
        <h2 className="text-3xl font-bold text-bark mb-4">Request Received!</h2>
        <p className="text-stone-500 mb-8">
          Thank you, {formData.firstName}. A GreenTree Certified Arborist will review your property at 
          <span className="font-bold text-palm block mt-2">{formData.address}</span>
          We usually respond within 24 hours.
        </p>
        <button 
          onClick={onReturn || onClose}
          className="w-full py-4 bg-palm text-white rounded-xl font-bold hover:bg-bark transition shadow-lg"
        >
          Return to Portal
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-2xl mx-auto border border-stone-100">
      <div className="bg-palm p-8 text-white relative">
        <div className="relative z-10">
          <h2 className="text-3xl font-bold">New Service Request</h2>
          <p className="text-leaf/80 mt-1 italic">Professional tree care starts here.</p>
        </div>
        <div className="absolute right-0 top-0 p-8 opacity-20">
          <Icons.Tree />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <label htmlFor="firstName" className="text-xs font-bold text-stone-500 uppercase tracking-wider">First Name</label>
            <input 
              required
              type="text"
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              placeholder="e.g. John"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none transition"
              value={formData.firstName}
              onChange={e => setFormData({...formData, firstName: e.target.value})}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="lastName" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Last Name</label>
            <input 
              required
              type="text"
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              placeholder="e.g. Appleseed"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none transition"
              value={formData.lastName}
              onChange={e => setFormData({...formData, lastName: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <label htmlFor="email" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Email Address</label>
            <input 
              required
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              placeholder="john@example.com"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none transition"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="phone" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Phone Number</label>
            <input 
              required
              type="tel"
              id="phone"
              name="phone"
              autoComplete="tel-national"
              placeholder="(555) 000-0000"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none transition"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="address" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Property Address</label>
          <input 
            required
            type="text"
            id="address"
            name="address"
            autoComplete="street-address"
            placeholder="Street, City, Zip Code"
            className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none transition"
            value={formData.address}
            onChange={e => setFormData({...formData, address: e.target.value})}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label htmlFor="propertyType" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Property Type</label>
            <select 
              id="propertyType"
              name="propertyType"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none appearance-none"
              onChange={e => setFormData({...formData, propertyType: e.target.value})}
            >
              <option>Residential</option>
              <option>Commercial</option>
              <option>Municipal</option>
              <option>HOA</option>
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="serviceType" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Primary Need</label>
            <select 
              id="serviceType"
              name="serviceType"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none appearance-none"
              onChange={e => setFormData({...formData, serviceType: e.target.value})}
            >
              <option>Pruning</option>
              <option>Removal</option>
              <option>Plant Health</option>
              <option>Emergency</option>
              <option>Consulting</option>
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="urgency" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Urgency</label>
            <select 
              id="urgency"
              name="urgency"
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none appearance-none"
              onChange={e => setFormData({...formData, urgency: e.target.value})}
            >
              <option>Low (Next 30 days)</option>
              <option>Medium (Next 14 days)</option>
              <option>High (Next 7 days)</option>
              <option>Emergency (ASAP)</option>
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="notes" className="text-xs font-bold text-stone-500 uppercase tracking-wider">Description & Special Instructions</label>
          <textarea 
            id="notes"
            name="notes"
            rows={3}
            placeholder="Tell us about your trees (e.g., 'Oak in back yard needs deadwooding')..."
            className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 focus:ring-2 focus:ring-gecko outline-none resize-none transition"
            onChange={e => setFormData({...formData, notes: e.target.value})}
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Upload Photos (Optional)</label>
          <div className="w-full p-4 bg-stone-50 rounded-xl border border-stone-100 border-dashed flex flex-col items-center justify-center text-center">
            <input 
              type="file" 
              multiple 
              accept="image/*"
              onChange={handleAttachmentUpload}
              className="hidden"
              id="client-photo-upload"
            />
            <label 
              htmlFor="client-photo-upload"
              className="cursor-pointer text-gecko font-bold hover:text-palm transition"
            >
              Click to browse
            </label>
            <p className="text-xs text-stone-400 mt-1">Upload photos of your trees or property</p>
          </div>
          
          {formData.attachments.length > 0 && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
              {formData.attachments.map((src, idx) => (
                <div key={idx} className="relative group">
                  <img src={src} alt="Upload preview" className="h-20 w-full object-cover rounded-xl border border-stone-200" />
                  <button 
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      attachments: prev.attachments.filter((_, i) => i !== idx)
                    }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow-md hover:bg-red-600 transition"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 flex items-center space-x-4">
          <button 
            type="button"
            onClick={onClose}
            className="px-8 py-4 text-stone-400 font-bold hover:bg-stone-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="flex-1 py-4 bg-gecko text-white rounded-xl font-bold text-lg hover:bg-palm transition transform active:scale-[0.98] shadow-xl shadow-gecko/20"
          >
            Submit Request
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerIntakeForm;
